import React, { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { DateTime } from 'luxon';
import { getGroupMaxSpeeds, bulkUpdateMaxSpeed } from '../api/performance.api';
import { IndividualMaxSpeed } from '../community.types';
import {
    useSpeedUnitPreference,
    convertMphToPreferred,
    convertPreferredToMph,
} from 'hooks/useSpeedUnitPreference';

interface MaxSpeedOverrideDialogProps {
    open: boolean;
    onClose: () => void;
    groupUuid: string;
    onSave: () => void;
}

const MaxSpeedOverrideDialog: React.FC<MaxSpeedOverrideDialogProps> = ({
    open,
    onClose,
    groupUuid,
    onSave,
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>('');
    const [individuals, setIndividuals] = useState<IndividualMaxSpeed[]>([]);
    const [overrides, setOverrides] = useState<{ [uuid: string]: string }>({});
    const { unit, label: unitLabel } = useSpeedUnitPreference();

    const loadMaxSpeeds = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            // Calculate date range for last 6 months
            const endDate = DateTime.now().toFormat('yyyy-MM-dd');
            const startDate = DateTime.now().minus({ months: 6 }).toFormat('yyyy-MM-dd');

            const response = await getGroupMaxSpeeds({
                group_uuid: groupUuid,
                start_date: startDate,
                end_date: endDate,
            });
            const data = response.data.data;
            setIndividuals(data);

            const initialOverrides: { [uuid: string]: string } = {};
            data.forEach((item) => {
                if (item.max_speed_override_mph != null) {
                    initialOverrides[item.individual.uuid] = convertMphToPreferred(
                        item.max_speed_override_mph,
                        unit,
                    ).toFixed(2);
                } else {
                    initialOverrides[item.individual.uuid] = '';
                }
            });
            setOverrides(initialOverrides);
        } catch (err) {
            console.error('Failed to load max speeds:', err);
            setError('Failed to load max speeds. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [groupUuid, unit]);

    useEffect(() => {
        if (open && groupUuid) {
            loadMaxSpeeds();
        }
    }, [open, groupUuid, loadMaxSpeeds]);

    const handleOverrideChange = (uuid: string, value: string) => {
        const maxValue = unit === 'fps' ? 59 : 40;
        if (value === '' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) <= maxValue)) {
            setOverrides((prev) => ({ ...prev, [uuid]: value }));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const payload: { [uuid: string]: number | null } = {};
            individuals.forEach((item) => {
                const newValue = overrides[item.individual.uuid];
                const currentValue = item.max_speed_override_mph;

                const newDisplayNum = newValue === '' ? null : parseFloat(newValue);
                const newMph =
                    newDisplayNum != null ? convertPreferredToMph(newDisplayNum, unit) : null;
                const hasChanged =
                    (newMph === null && currentValue !== null) ||
                    (newMph !== null && currentValue === null) ||
                    (newMph !== null &&
                        currentValue !== null &&
                        Math.abs(newMph - currentValue) > 0.01);

                if (hasChanged) {
                    payload[item.individual.uuid] = newMph;
                }
            });

            // Only make API call if there are changes
            if (Object.keys(payload).length > 0) {
                await bulkUpdateMaxSpeed({ individuals: payload });
            }

            onSave();
            onClose();
        } catch (err: any) {
            console.error('Failed to save overrides:', err);
            setError(
                err?.response?.data?.message ||
                    'Failed to save max speed overrides. Please try again.',
            );
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setOverrides({});
        setError('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
            <DialogTitle>Manage Max Speed Overrides</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Set max speed overrides for each athlete. Leave blank to remove
                            override. We will use the override value to calculate speed zones.
                            Calculated max speeds are based on the last 6 months of performance
                            data.
                        </Typography>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Athlete</TableCell>
                                        <TableCell align="right">
                                            Calculated Max Speed ({unitLabel.toUpperCase()})
                                        </TableCell>
                                        <TableCell align="right">
                                            Override ({unitLabel.toUpperCase()})
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {individuals.map((item) => (
                                        <TableRow key={item.individual.uuid}>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {item.individual.profile_photo && (
                                                        <img
                                                            src={item.individual.profile_photo}
                                                            alt={item.individual.name}
                                                            style={{
                                                                width: 32,
                                                                height: 32,
                                                                borderRadius: '50%',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    )}
                                                    <Typography variant="body2">
                                                        {item.individual.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2">
                                                    {item.calculated_max_speed_mph != null
                                                        ? convertMphToPreferred(
                                                              item.calculated_max_speed_mph,
                                                              unit,
                                                          ).toFixed(2)
                                                        : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TextField
                                                    size="small"
                                                    value={overrides[item.individual.uuid] || ''}
                                                    onChange={(e) =>
                                                        handleOverrideChange(
                                                            item.individual.uuid,
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="-"
                                                    type="text"
                                                    inputProps={{
                                                        min: 0,
                                                        max: 40,
                                                        step: 0.01,
                                                        style: { textAlign: 'right' },
                                                    }}
                                                    sx={{ width: 100 }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {individuals.length === 0 && !loading && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                                py={4}
                            >
                                No athletes found in this group.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={loading || saving}>
                    {saving ? 'Saving...' : 'Save & Export'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MaxSpeedOverrideDialog;
